import type { ZipProgress } from './mediaContracts';
import { saveBlob, type SaveMethod } from './fileSave';

/** Concurrent Twimg fetches — fast enough for ZIP/extract without hammering the CDN. */
export const MEDIA_FETCH_CONCURRENCY = 5;

/**
 * Fetches a media URL as a Blob.
 * Twimg CDN 403s when the page Referer is the app origin — omit referrer
 * (browsers forbid setting Referer/Origin headers manually).
 */
export async function fetchMediaBlob(url: string): Promise<Blob> {
  const response = await fetch(url, {
    referrer: 'no-referrer',
    referrerPolicy: 'no-referrer',
    headers: {
      Accept: '*/*',
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const blob = await response.blob();
  if (blob.size < 100) {
    throw new Error(`Downloaded file is too small (${blob.size} bytes)`);
  }

  return blob;
}

export async function downloadMediaUrl(
  url: string,
  filename: string,
  mimeType?: string
): Promise<SaveMethod> {
  const blob = await fetchMediaBlob(url);
  return saveBlob(blob, filename, mimeType);
}

/**
 * Run async work over items with a fixed concurrency pool.
 * Results keep input order. `onItemSettled` fires as each item finishes.
 */
export async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
  onItemSettled?: (index: number, result: R) => void
): Promise<R[]> {
  if (items.length === 0) return [];

  const limit = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const runWorker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      const result = await worker(items[index], index);
      results[index] = result;
      onItemSettled?.(index, result);
    }
  };

  await Promise.all(Array.from({ length: limit }, () => runWorker()));
  return results;
}

export interface ZipDownloadItem {
  url: string;
  filename: string;
}

export interface ZipDownloadResult {
  saved: number;
  total: number;
  zipName: string;
}

/**
 * Packs media URLs into a single zip and triggers a browser download.
 * Partial success: still saves if ≥1 file succeeded; throws only if zero succeeded.
 * Fetches run in a small concurrency pool for speed.
 */
export async function downloadItemsAsZip(
  items: ZipDownloadItem[],
  zipName: string,
  onProgress?: (p: ZipProgress) => void,
  concurrency: number = MEDIA_FETCH_CONCURRENCY
): Promise<ZipDownloadResult> {
  if (items.length === 0) {
    throw new Error('No media items to zip');
  }

  const { default: JSZip } = await import('jszip');

  const zip = new JSZip();
  let saved = 0;
  let settled = 0;
  const total = items.length;
  const usedNames = new Set<string>();

  // Resolve unique filenames up front so the fetch pool never races on names.
  const planned = items.map((item, i) => {
    let filename = item.filename || `file_${i + 1}`;
    if (usedNames.has(filename)) {
      const dot = filename.lastIndexOf('.');
      const base = dot > 0 ? filename.slice(0, dot) : filename;
      const ext = dot > 0 ? filename.slice(dot) : '';
      let n = 2;
      while (usedNames.has(`${base}_${n}${ext}`)) n++;
      filename = `${base}_${n}${ext}`;
    }
    usedNames.add(filename);
    return { url: item.url, filename };
  });

  onProgress?.({ current: 0, total, label: planned[0]?.filename });

  await mapPool(
    planned,
    concurrency,
    async (item) => {
      try {
        const blob = await fetchMediaBlob(item.url);
        zip.file(item.filename, blob);
        saved++;
        return item.filename;
      } catch (err) {
        console.error(`Error packing ${item.filename}:`, err);
        return null;
      }
    },
    (index) => {
      settled++;
      onProgress?.({
        current: settled,
        total,
        label: planned[index]?.filename,
      });
    }
  );

  if (saved === 0) {
    throw new Error('Failed to pack any media files into the zip');
  }

  const safeName = zipName.endsWith('.zip') ? zipName : `${zipName}.zip`;
  const zipBlob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/zip',
  });
  await saveBlob(zipBlob, safeName, 'application/zip');

  return { saved, total, zipName: safeName };
}
